/**
 * Author: Claude Code
 * Version: v3.0.72
 * Modified: 2025-09-19 Beijing Time
 * 
 * PhaserCombatSequencer: Phaser3原生Timeline战斗动画系统 (REFACTORED)
 * 替换CombatAnimationSequencer (478行) → PhaserCombatSequencer (优化后~300行)
 * 策略: 使用Phaser3 Timeline、Particles、Animation Events + 重构优化
 */

import * as Phaser from 'phaser';
import { CombatTheaterWindow } from '../ui/CombatTheaterWindow';
import { SpriteAnimationPlayer } from './SpriteAnimationPlayer';
import { SpriteAssetLoader } from './SpriteAssetLoader';
import { CharacterType, WeaponType, AnimationType, getFrameCount } from './spriteTypes';
import { getCharacterFrameKey, getWeaponFrameKey } from './utils/spriteKeyUtils';
import { getSlimeCharacterType } from './animationConfig';
import ClientTrace from '../../services/telemetry/ClientTrace';
import { CombatTimingState, summarizeCombatTiming, CombatStepKind, pushSegment } from './combat/CombatTiming';

const DEBUG_COMBAT = false;

// 🎯 类型定义
interface CombatEventData {
  combatId: string;
  attackerId: string;
  defenderId: string;
  damage?: number;
  isCritical?: boolean;
  weaponUsed?: { type: string };
  winner?: string;
  loser?: string;
  // 新增：当前行动者与颜色（避免错误猜测）
  activeAgentId?: string;
  attackerColor?: string;
  defenderColor?: string;
  // 🔧 新增：完整武器信息 (从COMBAT_STARTED事件中获取)
  attackerWeapon?: {
    itemId: string;
    type: string;
    name: string;
    category: string;
    status: string;
    owner: string;
    properties: {
      attackPower?: number;
      durability?: number;
      maxDurability?: number;
    };
  };
  defenderWeapon?: {
    itemId: string;
    type: string;
    name: string;
    category: string;
    status: string;
    owner: string;
    properties: {
      attackPower?: number;
      durability?: number;
      maxDurability?: number;
    };
  };
}

interface EffectConfig {
  fontSize: string;
  color: string;
  duration: number;
  tweenProps: { y: number; alpha: number };
}

interface ParticleConfig {
  speed: { min: number; max: number };
  scale: { start: number; end: number };
  lifespan: number;
  quantity?: number;
}

// 🎨 特效配置常量 (提取并类型化)
const EFFECT_CONFIG: Record<string, EffectConfig> = {
  damage_number: {
    fontSize: '16px',
    color: '#ff4444',
    duration: 800,
    tweenProps: { y: -40, alpha: 0 }
  },
  critical_hit: {
    fontSize: '18px',
    color: '#ffaa00',
    duration: 500,
    tweenProps: { y: -50, alpha: 0 }
  },
  miss: {
    fontSize: '16px',
    color: '#ffff00',
    duration: 600,
    tweenProps: { y: -30, alpha: 0 }
  }
} as const;

// 🌟 粒子效果配置常量 (提取并类型化)
const PARTICLE_CONFIG: Record<string, ParticleConfig> = {
  blood_splash: {
    speed: { min: 50, max: 100 },
    scale: { start: 1, end: 0 },
    lifespan: 300
  },
  weapon_glow: {
    speed: { min: 20, max: 40 },
    scale: { start: 0.5, end: 1.5 },
    lifespan: 400
  },
  critical_effect: {
    quantity: 10,
    speed: { min: 80, max: 120 },
    scale: { start: 1.5, end: 0.5 },
    lifespan: 600
  }
} as const;

// ⚙️ 动画配置常量
const ANIMATION_CONFIG = {
  ATTACK_DURATION: 600,
  HURT_DURATION: 500,
  HURT_DELAY_RATIO: 0.7, // 攻击动画70%时受击
  DEATH_DURATION: 1500,
  VICTORY_DURATION: 1000,
  VICTORY_DELAY: 500
} as const;

const MIN_VISIBLE_MS = 700;
const CLOSE_DEBOUNCE_MS = 250;
// 放宽最大可见时长，避免三段（伤害→Miss→伤害）在 800ms 步间距下被超时截断
const MAX_VISIBLE_MS = 5200;
const CLOSE_POLL_INTERVAL_MS = 120;

export class PhaserCombatSequencer {
  private scene: Phaser.Scene;
  private activeTimelines: Map<string, Phaser.Time.Timeline> = new Map();
  private particlePool: Map<string, Phaser.GameObjects.Particles.ParticleEmitter> = new Map();
  private theaterWindow: CombatTheaterWindow;
  private theaterSides?: { left: string; right: string };
  private colorsById: Map<string, string> = new Map();
  private weaponById: Map<string, WeaponType | null> = new Map();
  private theaterDeathLock = false; // 死亡闸门：开启后禁止任何攻/受/idle 切换
  private theaterOpenAt: Map<string, number> = new Map(); // 最小可见时长起点
  private deathShown: Set<string> = new Set(); // 已展示过死亡动画的 combatId
  private combatTiming: Map<string, CombatTimingState> = new Map(); // ⏱️ 战斗计时
  private stepsInFlight: Map<string, number> = new Map();
  private activeCombatId: string | null = null;
  private lastStepCompletedAt: Map<string, number> = new Map();
  private pendingClosures: Map<string, { loserId?: string; reason: string; requestedAt: number }>= new Map();
  private closeTimers: Map<string, Phaser.Time.TimerEvent> = new Map();
  private asyncStepsInFlight: Map<string, number> = new Map();
  private deathCompleted: Set<string> = new Set();
  private spriteAssetLoader: SpriteAssetLoader;
  private spriteAnimationPlayer: SpriteAnimationPlayer;
  // per-combat 串行队列（确保同一场战斗事件按顺序播放）
  private combatQueues: Map<string, Array<{ seq: number; kind: 'started'|'damage'|'miss'|'ended'; data: any }>> = new Map();
  private combatBusy: Set<string> = new Set();
  // Per-combat sequencing based on backend __seq
  private combatLastSeqProcessed: Map<string, number> = new Map();
  private combatCurrentSeq: Map<string, number> = new Map();
  private combatEnded: Set<string> = new Set();
  // 战斗期间暂存HP变化，等到受击帧统一应用，保证与动画同步
  // HP事件按顺序排队，受击帧按次序消费，避免“首击应用终局值”
  private hpQueue: Map<string, Array<{ current: number; max: number }>> = new Map();
  // 不再缓存剧院未激活时的健康事件，避免使用过期值导致错误的起始HP
  private lastCloseDebugAt: Map<string, number> = new Map();
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.theaterWindow = new CombatTheaterWindow(scene);
    this.spriteAssetLoader = new SpriteAssetLoader(scene);
    this.spriteAnimationPlayer = new SpriteAnimationPlayer(scene, this.spriteAssetLoader);
    this.setupAnimationEventHandlers();
  }
  
  // 🏁 处理战斗开始事件 (增强: 集成剧院窗口)
  handleCombatStarted(combatData: CombatEventData): void {
    const cid = (combatData as any).combatId || 'unknown';
    if (this.combatEnded.has(cid)) return;
    this.enqueueCombat(cid, { kind: 'started', data: combatData });
  }

  private _doCombatStarted(combatData: CombatEventData): void {
    const { combatId, attackerId, defenderId } = combatData;

    // 🔍 详细日志：输出接收到的完整参数
    // 接收到战斗开始参数（简化日志）

    if (DEBUG_COMBAT) console.log(`🎭 PhaserCombatSequencer: 战斗开始 ${attackerId} vs ${defenderId}`);
    
    // 启动剧院模式 - 固定整场：左=defender，右=attacker（不随回合交换）
    const leftId = defenderId;
    const rightId = attackerId;
    this.theaterSides = { left: leftId, right: rightId };
    const leftColor = combatData.defenderColor || this.fallbackColor(leftId);
    const rightColor = combatData.attackerColor || this.fallbackColor(rightId);
    this.colorsById.set(leftId, leftColor);
    this.colorsById.set(rightId, rightColor);
    
    this.theaterWindow.createTheaterMode(
      leftId,
      rightId,
      leftColor,
      rightColor,
      { mode: 'pin', pinnedAgentId: rightId, pinnedOffsetY: 90 }
    );
    this.theaterDeathLock = false;

    // 初始化HP优先级：
    // 1) 若COMBAT_STARTED携带快照(attackerHealth/defenderHealth)，先用它作为战斗起始HP（后端权威快照）
    // 2) 否则，若之前缓存过 AGENT_HEALTH_CHANGED（pendingHealth），再用缓存避免晚到才显示
    // 3) 后续所有变化继续依赖 AGENT_HEALTH_CHANGED 实时同步
    try {
      const atkH: any = (combatData as any).attackerHealth;
      const defH: any = (combatData as any).defenderHealth;
      const defStartOk = defH && typeof defH.current === 'number' && typeof defH.max === 'number';
      const atkStartOk = atkH && typeof atkH.current === 'number' && typeof atkH.max === 'number';

      if (defStartOk) {
        this.theaterWindow.setHP('left', defH.current, defH.max);
        console.log(`💊 剧院HP(起始快照): left=${defH.current}/${defH.max}`);
      }
      if (atkStartOk) {
        this.theaterWindow.setHP('right', atkH.current, atkH.max);
        console.log(`💊 剧院HP(起始快照): right=${atkH.current}/${atkH.max}`);
      }
      // 若某侧没有起始快照，则保留为 --/--，等待战斗后续的 AGENT_HEALTH_CHANGED（严格避免使用历史缓存）
    } catch {}

    // 🔧 从COMBAT_STARTED事件中提取武器数据

    if (combatData.attackerWeapon) {
      const rawType = (combatData.attackerWeapon as any).weaponType || (combatData.attackerWeapon as any).type;
      const attackerWeaponType = this.mapWeaponString(String(rawType || 'UNARMED').toUpperCase());
      this.weaponById.set(attackerId, attackerWeaponType);
    } else {
      // no-op
    }

    if (combatData.defenderWeapon) {
      const rawType = (combatData.defenderWeapon as any).weaponType || (combatData.defenderWeapon as any).type;
      const defenderWeaponType = this.mapWeaponString(String(rawType || 'UNARMED').toUpperCase());
      this.weaponById.set(defenderId, defenderWeaponType);
    } else {
      // no-op
    }
    // Theater初始化完毕（简化日志）

    // 创建战斗时间轴 (在剧院窗口内执行)
    const timeline = this.createCombatTimeline();

    // 添加对峙阶段动画 (剧院窗口内的动画)
    this.addTheaterIdleAnimationStep(timeline, leftColor, rightColor);

    this.playAndStoreTimeline(combatId, timeline);
    // 记录剧院打开时间 + 初始化计时
    const nowOpen = Date.now();
    this.theaterOpenAt.set(combatId, nowOpen);
    this.combatTiming.set(combatId, { combatId, openedAt: nowOpen, steps: [] });
    this.stepsInFlight.set(combatId, 0);
    this.lastStepCompletedAt.set(combatId, nowOpen);
    this.asyncStepsInFlight.set(combatId, 0);
    this.deathCompleted.delete(combatId);
    this.activeCombatId = combatId;
  }
  
  // ⚔️ 处理战斗伤害事件 (增强: 剧院攻击动画)
  handleCombatDamage(combatData: CombatEventData): void {
    const cid = (combatData as any).combatId || 'unknown';
    if (this.combatEnded.has(cid)) return;
    this.enqueueCombat(cid, { kind: 'damage', data: combatData });
  }

  private _doCombatDamage(combatData: CombatEventData): void {
    const { combatId, attackerId, defenderId, damage = 0, isCritical = false } = combatData;
    // 严格顺序：未收到 COMBAT_STARTED 前不允许播放伤害事件
    if (!this.theaterWindow.isTheaterActive()) {
      console.warn('Damage ignored: theater not active for combatId', combatId);
      this.onCombatStepComplete(combatId);
      return;
    }
    const timeline = this.createCombatTimeline();
    // 记录攻击者武器（决定是否走持武器分层）
    if ((combatData as any).weaponUsed) {
      const wu: any = (combatData as any).weaponUsed;
      const rawType = wu.weaponType || wu.type;
      if (rawType) {
        this.weaponById.set(attackerId, this.mapWeaponString(String(rawType).toUpperCase()));
      }
    }
    
    // 如果剧院模式激活，使用剧院攻击动画（不再重建/置换）
    if (this.theaterWindow.isTheaterActive() && this.theaterSides) {
      const attackerSide = this.getTheaterSideForAgent(attackerId);
      const defenderSide = attackerSide === 'left' ? 'right' : 'left';
      const attackerColor = this.colorsById.get(attackerId) || this.colorForAgent(attackerId, combatData);
      const hasWeapon = this.hasWeapon(attackerId);

      // 使用剧院攻击动画（优先调用成熟的武器动画模块）
      // 记录武器类型供攻击动画模块使用
      // 记录武器类型供攻击动画模块使用
      if (hasWeapon) {
        this.addTheaterAttackAnimationStep(timeline, attackerSide, attackerColor, attackerId);
      } else {
        this.addTheaterRushAttackStep(timeline, attackerSide, attackerColor);
      }
      // 确保HP条不被分层精灵遮住
      try { this.theaterWindow.bringHPBarsToFront(); } catch {}

      // 在受击帧对齐HP更新：优先使用事件自带的剩余HP；若无，则消费队列
      const applyHpAtHurt = () => {
        const evtDef = (combatData as any).defenderRemainingHealth;
        if (evtDef && typeof evtDef.current === 'number' && typeof evtDef.max === 'number') {
          this.theaterWindow.setHP(defenderSide, evtDef.current, evtDef.max);
          console.log(`💊 剧院HP(对齐受击帧:事件): ${defenderSide}侧 => ${evtDef.current}/${evtDef.max}`);
        } else {
          const q = this.hpQueue.get(defenderId);
          const defHp = q && q.length > 0 ? q.shift()! : null;
          if (defHp) {
            this.theaterWindow.setHP(defenderSide, defHp.current, defHp.max);
            console.log(`💊 剧院HP(对齐受击帧:排队): ${defenderSide}侧 => ${defHp.current}/${defHp.max}`);
          }
        }
        // 注意：一条伤害事件只消费被击方（defenderId）队列；
        // 反击将作为后续一条伤害事件再消费另一侧队列，避免两侧同帧跳变导致视觉倍速
        try { this.theaterWindow.bringHPBarsToFront(); } catch {}
      };

      timeline.add({
        at: ANIMATION_CONFIG.ATTACK_DURATION * ANIMATION_CONFIG.HURT_DELAY_RATIO,
        run: applyHpAtHurt
      });

      // 在剧院内播放受击反馈（代替地图图层受击）
      this.addTheaterHurtAnimationStep(timeline, defenderSide);

      // 若该次伤害为致死，追加死亡动画锁与收尾
      try {
        const evtDef = (combatData as any).defenderRemainingHealth;
        if (evtDef && typeof evtDef.current === 'number' && evtDef.current <= 0) {
          // 在受击帧后紧接着播放死亡动画
          timeline.add({
            at: ANIMATION_CONFIG.ATTACK_DURATION * ANIMATION_CONFIG.HURT_DELAY_RATIO + 50,
            run: () => {
              if (this.theaterDeathLock) return;
              const loserSide = defenderSide;
              const loserId = defenderId;
              const loserColor = this.colorsById.get(loserId) || this.fallbackColor(loserId);
              // 仅播放死亡动画，不关闭剧院；收口由 ENDED 统一触发
              this.addTheaterDeathAnimationStep(timeline, loserSide, loserColor, combatId);
              this.deathShown.add(combatId);
            }
          });
          // 直接开启收口门，避免等待 ENDED 才开始收口
          this.requestCombatClosure(combatId, defenderId, 'lethal_damage');
        }
      } catch {}

      if (DEBUG_COMBAT) console.log(`⚔️ 剧院攻击: ${attackerSide}→${defenderSide}, damage=${damage}`);
    } else {
      // 若缺少 sides（异常），也不播放
      console.warn('Damage ignored: theater sides missing for combatId', combatId);
      this.onCombatStepComplete(combatId);
      return;
    }
    
    this.playAndStoreTimeline(combatId, timeline, 'damage');
  }
  
  // 💨 处理战斗失误事件 (重构: 简化逻辑)
  handleCombatMiss(missData: CombatEventData): void {
    const cid = (missData as any).combatId || 'unknown';
    if (this.combatEnded.has(cid)) return;
    this.enqueueCombat(cid, { kind: 'miss', data: missData });
  }

  private _doCombatMiss(missData: CombatEventData): void {
    const { combatId, attackerId, defenderId } = missData;
    const timeline = this.createCombatTimeline();

    // 🎯 复用handleCombatDamage结构，miss仅替换hurt步骤为miss文字
    if (this.theaterWindow.isTheaterActive() && this.theaterSides) {
      const attackerSide = this.getTheaterSideForAgent(attackerId);
      const defenderSide = attackerSide === 'left' ? 'right' : 'left';
      const attackerColor = this.colorsById.get(attackerId) || this.colorForAgent(attackerId, missData);
      const hasWeapon = this.hasWeapon(attackerId);

      if (hasWeapon) {
        this.addTheaterAttackAnimationStep(timeline, attackerSide, attackerColor, attackerId);
      } else {
        this.addTheaterRushAttackStep(timeline, attackerSide, attackerColor);
      }

      // 用miss文字步骤替换hurt动画步骤
      this.addTheaterMissTextStep(timeline, defenderSide);
    } else {
      console.warn('Miss ignored: theater not active for combatId', combatId);
      this.onCombatStepComplete(combatId);
      return;
    }

    this.playAndStoreTimeline(combatId, timeline, 'miss');
  }
  
  // 🏆 处理战斗结束事件 (增强: 剧院死亡动画)
  handleCombatEnded(endData: CombatEventData): void {
    const cid = (endData as any).combatId || 'unknown';
    if (this.combatEnded.has(cid)) return;
    this.enqueueCombat(cid, { kind: 'ended', data: endData });
  }

  private _doCombatEnded(endData: CombatEventData): void {
    const { combatId, winner = '', loser = '' } = endData;

    if (DEBUG_COMBAT) console.log(`🏆 PhaserCombatSequencer: 战斗结束 ${winner} 胜利, ${loser} 死亡`);
    console.log(`[DEATH] theaterActive=${this.theaterWindow.isTheaterActive()} sides=${JSON.stringify(this.theaterSides || {})}`);

    if (this.theaterWindow.isTheaterActive()) {
      if (!this.deathShown.has(combatId)) {
        const timeline = this.createCombatTimeline();
        const loserSide: 'left' | 'right' = this.theaterSides && this.theaterSides.left === loser ? 'left' : 'right';
        const loserColor = this.colorsById.get(loser) || this.fallbackColor(loser);
        this.addTheaterDeathAnimationStep(timeline, loserSide, loserColor, combatId);
        this.playAndStoreTimeline(combatId, timeline, 'death');
        this.deathShown.add(combatId);
      }
      this.requestCombatClosure(combatId, loser || undefined, 'combat_ended');
    } else {
      const timeline = this.createCombatTimeline();
      this.addDeathAnimationStep(timeline, loser);
      this.addVictoryAnimationStep(timeline, winner, combatId);
      this.playAndStoreTimeline(combatId, timeline, 'death');
      this.requestCombatClosure(combatId, loser || undefined, 'combat_fallback');
    }
    this.combatEnded.add(combatId);
  }

  // 🏗️ 创建Timeline辅助方法
  private createCombatTimeline(): Phaser.Time.Timeline {
    // 根据Phaser3官方文档：使用 this.scene.add.timeline()
    return this.scene.add.timeline();
  }

  // 🎬 添加动画步骤方法 (使用Timeline事件配置而非Tween配置)
  private addIdleAnimationStep(timeline: Phaser.Time.Timeline, agentId: string): void {
    timeline.add({
      at: 0,
      run: () => this.playIdleAnimation(agentId)
    });
  }

  private addAttackAnimationStep(timeline: Phaser.Time.Timeline, attackerId: string): void {
    timeline.add({
      at: 0,
      run: () => this.playAttackAnimation(attackerId)
    });
  }

  private addHurtAnimationStep(timeline: Phaser.Time.Timeline, defenderId: string, damage: number, isCritical: boolean, delay: number): void {
    timeline.add({
      at: delay,
      run: () => {
        this.playHurtAnimation(defenderId);
        this.playBloodSplash(defenderId, 0, 0);
        isCritical ? this.playCriticalEffect(defenderId, damage) : this.playDamageNumber(defenderId, damage);
      }
    });
  }

  private addMissAnimationStep(timeline: Phaser.Time.Timeline, attackerId: string): void {
    timeline.add({
      at: 0,
      run: () => {
        this.playAttackAnimation(attackerId);
        this.playMissEffect(attackerId);
      }
    });
  }

  private addDeathAnimationStep(timeline: Phaser.Time.Timeline, loserId: string): void {
    timeline.add({
      at: 0,
      run: () => this.playDeathAnimation(loserId)
    });
  }

  private addVictoryAnimationStep(timeline: Phaser.Time.Timeline, winnerId: string, combatId: string): void {
    timeline.add({
      at: ANIMATION_CONFIG.VICTORY_DELAY,
      run: () => {
        this.playIdleAnimation(winnerId);
        // 在胜利动画结束后清理
        this.scene.time.delayedCall(ANIMATION_CONFIG.VICTORY_DURATION, () => {
          this.cleanupCombat(combatId);
        });
      }
    });
  }

  private playAndStoreTimeline(combatId: string, timeline: Phaser.Time.Timeline, kind?: CombatStepKind): void {
    try {
      const current = this.stepsInFlight.get(combatId) || 0;
      this.stepsInFlight.set(combatId, current + 1);
      const needsAsyncTracking = kind === 'damage' || kind === 'miss' || kind === 'death';
      if (needsAsyncTracking) {
        this.incrementAsyncStep(combatId);
      }
      const stepStartAt = (kind === 'damage' || kind === 'miss' || kind === 'death') ? Date.now() : 0;
      timeline.once('complete', () => {
        // 立即处理下一步，延迟由Timeline内部的'at'参数控制，避免delayedCall导致事件泄露
        this.onCombatStepComplete(combatId);
        const remaining = this.stepsInFlight.get(combatId) || 0;
        this.stepsInFlight.set(combatId, Math.max(0, remaining - 1));
        const now = Date.now();
        this.lastStepCompletedAt.set(combatId, now);
        if (kind === 'damage' || kind === 'miss') {
          const state = this.combatTiming.get(combatId);
          if (state) state.steps.push({ kind, t: now });
        }
        // 精确记录单段的可视播放时长（attack/miss/death）
        if (kind === 'damage' || kind === 'miss' || kind === 'death') {
          try {
            const st = this.combatTiming.get(combatId);
            if (st && stepStartAt > 0) {
              pushSegment(st as any, kind as any, stepStartAt, now);
              const dur = Math.max(0, now - stepStartAt);
              try { ClientTrace.log('playback','playback','COMBAT_STEP', { details: { combatId, kind, durMs: dur, startedAt: stepStartAt, endedAt: now } }); } catch {}
            }
          } catch {}
        }
        if (kind === 'death') {
          this.deathCompleted.add(combatId);
        }
        if (needsAsyncTracking) {
          this.decrementAsyncStep(combatId);
        }
        // 🛰️ 广播战斗步骤播放完成（供上层等待真实结束）
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ABR_COMBAT_STEP_DONE', {
              detail: { combatId, timestamp: Date.now() }
            }));
          }
        } catch {}
        this.evaluateCloseConditions(combatId);
      });
    } catch {}
    timeline.play();
    this.activeTimelines.set(combatId, timeline);
  }

  private enqueueCombat(combatId: string, entry: { kind: 'started'|'damage'|'miss'|'ended'; data: any }) {
    if (!this.combatQueues.has(combatId)) this.combatQueues.set(combatId, []);
    const queue = this.combatQueues.get(combatId)!;
    try {
      let seq = entry?.data?.__seq ?? entry?.data?.sequence;
      if (typeof seq !== 'number') {
        const cur = this.combatCurrentSeq.get(combatId) || 0;
        seq = cur + 1;
        this.combatCurrentSeq.set(combatId, seq);
      } else {
        // 同步本地水位
        const cur = this.combatCurrentSeq.get(combatId) || 0;
        if (seq > cur) this.combatCurrentSeq.set(combatId, seq);
      }
      entry.data.__seq = seq;
      queue.push({ seq, kind: entry.kind, data: entry.data });
      ClientTrace.log('playback','playback','COMBAT_QUEUE_PUSH', {
        details: {
          combatId,
          kind: entry.kind,
          seq,
          queueLen: queue.length
        }
      });
    } catch (err) {
      // 兜底：仍推入（seq 置 0），避免播放中断；同时记录警告
      queue.push({ seq: 0 as any, kind: entry.kind, data: entry.data });
      console.warn('[CombatSequencer] queue push failed', err);
    }
    if (!this.combatBusy.has(combatId)) {
      this.processNextCombatStep(combatId);
    }
  }

  private processNextCombatStep(combatId: string) {
    const q = this.combatQueues.get(combatId) || [];
    if (q.length === 0) { this.combatBusy.delete(combatId); return; }
    this.combatBusy.add(combatId);
    const next = q.shift()!;
    try {
      const seq = next?.data?.__seq;
      ClientTrace.log('playback','playback','COMBAT_QUEUE_POP', {
        details: {
          combatId,
          kind: next.kind,
          seq,
          queueLen: q.length
        }
      });
    } catch {}
    switch (next.kind) {
      case 'started': this._doCombatStarted(next.data); break;
      case 'damage': this._doCombatDamage(next.data); break;
      case 'miss': this._doCombatMiss(next.data); break;
      case 'ended': this._doCombatEnded(next.data); break;
    }
  }

  private onCombatStepComplete(combatId: string) {
    // 步骤完成后继续队列（若已END则清空队列）
    if (this.combatEnded.has(combatId)) {
      this.combatQueues.delete(combatId);
      this.combatBusy.delete(combatId);
      return;
    }
    // ⚡ 添加攻击间隔延迟，避免攻击动画过快连续播放
    const COMBAT_STEP_DELAY = 800; // 800ms间隔，让每次攻击更清晰
    this.scene.time.delayedCall(COMBAT_STEP_DELAY, () => {
      this.processNextCombatStep(combatId);
    });
  }
  
  // 🩸 血溅特效 (重构: 配置驱动 + 错误处理)
  playBloodSplash(agentId: string, x: number, y: number): void {
    this.createParticleEffect('blood_splash', { x, y, quantity: 5 });
  }
  
  // ⚡ 武器光效 (重构: 简化 + 池化管理)
  playWeaponGlow(agentId: string, weaponType: string): void {
    const emitter = this.createParticleEffect('weapon_glow', { x: 100, y: 100 });
    if (emitter) {
      emitter.setPosition(100, 100); // 保持与原实现的兼容性
      this.particlePool.set(`${agentId}_weapon`, emitter);
    }
  }
  
  // 💥 暴击特效 (重构: 统一粒子创建)
  playCriticalEffect(agentId: string, damage: number): void {
    const emitter = this.createParticleEffect('critical_effect', { x: 100, y: 100 });
    emitter?.explode();
  }

  // 🔢 伤害数字显示 (重构: 统一文本创建)
  playDamageNumber(agentId: string, damage: number): void {
    this.createTextEffect('damage_number', `-${damage}`, { x: 100, y: 100 });
  }
  
  // 💨 失误特效 (重构: 统一文本创建)
  playMissEffect(agentId: string): void {
    this.createTextEffect('miss', 'MISS!', { x: 100, y: 100 });
  }

  // 🌟 通用粒子效果创建器 (新增: DRY原则)
  private createParticleEffect(effectType: string, options: { x: number; y: number; quantity?: number }): Phaser.GameObjects.Particles.ParticleEmitter | null {
    const config = PARTICLE_CONFIG[effectType];
    if (!config) {
      console.warn(`Unknown particle effect: ${effectType}`);
      return null;
    }

    const particles = this.scene.add.particles(0, 0, `${effectType}-particle`, {
      x: options.x,
      y: options.y,
      speed: config.speed,
      scale: config.scale,
      lifespan: config.lifespan
    });

    const emitter = particles.createEmitter({
      x: options.x,
      y: options.y,
      speed: config.speed,
      scale: config.scale,
      lifespan: config.lifespan,
      quantity: options.quantity || config.quantity || 1
    });

    emitter.start();
    return emitter;
  }

  // 📝 通用文本效果创建器 (新增: DRY原则)
  private createTextEffect(effectType: string, text: string, position: { x: number; y: number }): void {
    const config = EFFECT_CONFIG[effectType];
    if (!config) {
      console.warn(`Unknown text effect: ${effectType}`);
      return;
    }

    const textObj = this.scene.add.text(position.x, position.y, text, {
      fontSize: config.fontSize,
      color: config.color
    });
    
    textObj.setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: textObj,
      y: textObj.y + config.tweenProps.y,
      alpha: config.tweenProps.alpha,
      duration: config.duration,
      onComplete: () => textObj.destroy()
    });
  }
  
  // 🎭 设置动画事件处理器 (重构: 类型安全 + 错误处理)
  setupAnimationEventHandlers(): void {
    this.scene.anims.on('animationcomplete', (event: { key: string }) => {
      if (event.key === 'attack') {
        this.triggerNextAction(event);
      }
    });
  }
  
  // 🎯 触发下一动作 (重构: 优化逻辑)
  private triggerNextAction(event: { key: string }): void {
    // Timeline会自动处理序列，这里预留扩展点
    const timeline = this.createCombatTimeline();
    timeline.add({
      targets: {},
      duration: 100,
      onComplete: () => {
        // 可扩展的后续动作逻辑
      }
    });
  }

  // 🔧 公共接口方法
  
  // 获取特效配置 (重构: 类型安全)
  getEffectConfig(effectType: string): EffectConfig | null {
    return EFFECT_CONFIG[effectType] || null;
  }
  
  // 获取活跃的Timeline (重构: 只读访问)
  getActiveTimelines(): ReadonlyMap<string, Phaser.Time.Timeline> {
    return this.activeTimelines;
  }
  
  // 获取粒子池 (重构: 只读访问)
  getParticlePool(): ReadonlyMap<string, Phaser.GameObjects.Particles.ParticleEmitter> {
    return this.particlePool;
  }

  // ⏸️ 战斗控制方法
  
  // 暂停所有战斗 (重构: 防御性编程)
  pauseAllCombat(): void {
    this.executeOnAllTimelines(timeline => timeline.pause?.());
  }
  
  // 恢复所有战斗 (重构: 防御性编程)
  resumeAllCombat(): void {
    this.executeOnAllTimelines(timeline => timeline.resume?.());
  }

  // 🏗️ 内部工具方法
  
  // 对所有timeline执行操作 (新增: DRY原则)
  private executeOnAllTimelines(action: (timeline: Phaser.Time.Timeline) => void): void {
    this.activeTimelines.forEach(timeline => {
      if (timeline) {
        try {
          action(timeline);
        } catch (error) {
          console.warn('Timeline operation failed:', error);
        }
      }
    });
  }
  
  // 📊 性能和调试方法
  
  // 获取性能指标 (重构: 更详细的指标)
  getPerformanceMetrics(): {
    activeTimelines: number;
    activeParticles: number;
    memoryEstimate: number;
    timestamp: number;
  } {
    return {
      activeTimelines: this.activeTimelines.size,
      activeParticles: this.particlePool.size,
      memoryEstimate: this.calculateMemoryEstimate(),
      timestamp: Date.now()
    };
  }

  // 计算内存估算 (新增: 更准确的计算)
  private calculateMemoryEstimate(): number {
    const TIMELINE_MEMORY = 1024; // bytes per timeline
    const PARTICLE_MEMORY = 512; // bytes per particle emitter
    return this.activeTimelines.size * TIMELINE_MEMORY + this.particlePool.size * PARTICLE_MEMORY;
  }
  
  // 向后兼容性：获取调试信息 (重构: 更丰富的信息)
  getDebugInfo(): {
    activeTimelines: string[];
    particlePool: number;
    performanceMetrics: ReturnType<typeof this.getPerformanceMetrics>;
    version: string;
  } {
    return {
      activeTimelines: Array.from(this.activeTimelines.keys()),
      particlePool: this.particlePool.size,
      performanceMetrics: this.getPerformanceMetrics(),
      version: 'v3.0.70-refactored'
    };
  }

  // 🧹 清理方法
  
  // 清理战斗 (重构: 安全清理 + 日志)
  private cleanupCombat(combatId: string): void {
    const timeline = this.activeTimelines.get(combatId);
    if (timeline) {
      try {
        // 检查Timeline是否已经被destroy（避免重复destroy导致错误）
        const alreadyDestroyed = !(timeline as any).scene || (timeline as any).scene === null;
        if (alreadyDestroyed) {
          console.debug(`Combat ${combatId} timeline already destroyed, skipping cleanup`);
          this.activeTimelines.delete(combatId);
          return;
        }

        // 先stop再destroy，避免内部状态异常导致 "Cannot read properties of null (reading 'events')" 错误
        if (typeof (timeline as any).stop === 'function') {
          (timeline as any).stop();
        }
        if (typeof timeline.destroy === 'function') {
          timeline.destroy();
        }
        this.activeTimelines.delete(combatId);
        console.debug(`Combat ${combatId} cleaned up successfully`);
      } catch (error) {
        console.warn(`Failed to cleanup combat ${combatId}: ${(error as any)?.message || error}`);
        // 即使destroy失败也要从Map中移除，避免内存泄露
        this.activeTimelines.delete(combatId);
      }
    }
    // 清理状态缓存
    try { this.combatQueues.delete(combatId); } catch {}
    try { this.combatBusy.delete(combatId); } catch {}
    try { this.combatEnded.delete(combatId); } catch {}
  }

  // 🎮 BattleScene集成方法 (INTEGRATION)
  
  // 获取Agent精灵 (集成BattleScene)
  private getAgentSprite(agentId: string): Record<string, unknown> {
    // 通过Scene访问Agent容器
    const battleScene = this.scene as any;
    if (battleScene.agents && battleScene.agents.has(agentId)) {
      const agentContainer = battleScene.agents.get(agentId);
      return agentContainer || {};
    }
    
    // 兼容PhaserAnimationManager
    if (battleScene.frameAnimationManager) {
      const container = battleScene.frameAnimationManager.getAgentContainer(agentId);
      if (container) return container;
    }
    
    console.warn(`Agent sprite not found: ${agentId}`);
    return {}; // 默认返回空对象以避免错误
  }
  
  // 动画播放方法 (集成实际动画系统)
  private playIdleAnimation(agentId: string): void {
    const battleScene = this.scene as any;
    if (battleScene.frameAnimationManager) {
      battleScene.frameAnimationManager.playAnimation(agentId, 'idle');
    }
  }
  
  private playAttackAnimation(agentId: string): void {
    const battleScene = this.scene as any;
    if (battleScene.frameAnimationManager) {
      battleScene.frameAnimationManager.playAnimation(agentId, 'attack');
    }
  }
  
  private playHurtAnimation(agentId: string): void {
    const battleScene = this.scene as any;
    if (battleScene.frameAnimationManager) {
      battleScene.frameAnimationManager.playAnimation(agentId, 'hurt');
    }
  }
  
  private playDeathAnimation(agentId: string): void {
    console.log(`💀 playDeathAnimation调用: ${agentId}`);
    const battleScene = this.scene as any;
    console.log(`💀 battleScene检查: exists=${!!battleScene}`);
    console.log(`💀 frameAnimationManager检查: exists=${!!battleScene?.frameAnimationManager}`);
    console.log(`💀 phaserAnimationManager检查: exists=${!!battleScene?.phaserAnimationManager}`);

    // 🎯 绕过Engine死亡锁定，直接播放主地图死亡动画
    let animationPlayed = false;

    // 方案1: 使用frameAnimationManager (原有系统)
    if (battleScene?.frameAnimationManager) {
      try {
        console.log(`💀 方案1-frameAnimationManager: ${agentId}`);
        battleScene.frameAnimationManager.playAnimation(agentId, 'die');
        animationPlayed = true;
        console.log(`✅ 主地图死亡动画播放成功(方案1): ${agentId}`);
        // 调度容器清理 (死亡动画约2秒)
        this.scheduleAgentCleanup(agentId, 2000);
      } catch (error) {
        console.error(`❌ 方案1失败:`, error);
      }
    }

    // 方案2: 使用PhaserAnimationManager (新系统)
    if (!animationPlayed && battleScene?.phaserAnimationManager) {
      try {
        console.log(`💀 方案2-PhaserAnimationManager: ${agentId}`);
        battleScene.phaserAnimationManager.playAnimation(agentId, AnimationType.DIE);
        animationPlayed = true;
        console.log(`✅ 主地图死亡动画播放成功(方案2): ${agentId}`);
        // 调度容器清理 (死亡动画约2秒)
        this.scheduleAgentCleanup(agentId, 2000);
      } catch (error) {
        console.error(`❌ 方案2失败:`, error);
      }
    }

    // 方案3: 直接操作agent容器 (最后手段)
    if (!animationPlayed) {
      try {
        console.log(`💀 方案3-直接容器操作: ${agentId}`);
        const agentContainer = battleScene?.agentContainers?.get(agentId);
        if (agentContainer) {
          // 简单的淡出效果模拟死亡
          battleScene.tweens.add({
            targets: agentContainer,
            alpha: 0,
            duration: 1000,
            onComplete: () => {
              console.log(`✅ 主地图死亡动画播放成功(方案3-淡出): ${agentId}`);
              // 淡出完成后立即清理
              this.cleanupDeadAgentContainer(agentId);
            }
          });
          animationPlayed = true;
        }
      } catch (error) {
        console.error(`❌ 方案3失败:`, error);
      }
    }

    if (!animationPlayed) {
      console.error(`❌ 所有主地图死亡动画方案都失败了: ${agentId}`);
      // 即使动画失败，也要清理容器避免残留
      this.scheduleAgentCleanup(agentId, 500);
    }
  }

  // 🕐 容器清理调度方法
  private scheduleAgentCleanup(agentId: string, delay: number): void {
    console.log(`⏱️ 调度agent容器清理: ${agentId} 延迟${delay}ms`);

    this.scene.time.delayedCall(delay, () => {
      this.cleanupDeadAgentContainer(agentId);
    });
  }

  // 🧹 完整的死亡agent容器清理
  private cleanupDeadAgentContainer(agentId: string): void {
    const battleScene = this.scene as any;

    console.log(`🧹 开始清理死亡agent容器: ${agentId}`);

    // 1. PhaserAnimationManager容器清理 (包含agent容器、标签、武器等)
    if (battleScene?.phaserAnimationManager) {
      try {
        battleScene.phaserAnimationManager.removeAgent(agentId);
        console.log(`✅ PhaserAnimationManager已清除agent容器: ${agentId}`);
      } catch (error) {
        console.error(`❌ PhaserAnimationManager清理失败:`, error);
      }
    }

    // 2. BattleScene agents Map清理
    if (battleScene?.removeAgent) {
      try {
        battleScene.removeAgent(agentId);
        console.log(`✅ BattleScene已清除agents Map: ${agentId}`);
      } catch (error) {
        console.error(`❌ BattleScene agents清理失败:`, error);
      }
    }

    // 3. BattleScene sprite pool清理 (备用)
    if (battleScene?.removeAgentSprite) {
      try {
        battleScene.removeAgentSprite(agentId);
        console.log(`✅ BattleScene已清除sprite pool: ${agentId}`);
      } catch (error) {
        console.error(`❌ BattleScene sprite pool清理失败:`, error);
      }
    }

    console.log(`🎉 死亡agent容器完全清理完成: ${agentId}`);
    // 🛰️ 广播淘汰完成（剧场路径）
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ABR_ELIMINATION_DONE', { detail: { agentId, timestamp: Date.now() } }));
      }
    } catch {}

    if (this.activeCombatId === combatId) {
      this.activeCombatId = null;
    }
  }

  // 🎭 剧院模式相关方法
  
  /**
   * 剧院模式idle动画步骤
   */
  private addTheaterIdleAnimationStep(timeline: Phaser.Time.Timeline, leftColor: string, rightColor: string): void {
    timeline.add({
      at: 0,
      run: () => {
        // 根据是否持武器选择Idle形态
        const leftId = this.theaterSides!.left;
        const rightId = this.theaterSides!.right;
        // 选择正确的idle动画类型
        if (this.hasWeapon(leftId)) {
          this.spawnOrPlayLayeredIdle('left', leftColor);
        } else {
          this.theaterWindow.playIdle('left');
        }

        if (this.hasWeapon(rightId)) {
          this.spawnOrPlayLayeredIdle('right', rightColor);
        } else {
          this.theaterWindow.playIdle('right');
        }
      }
    });
  }

  /**
   * 剧院模式攻击动画 - 使用剧院窗口的动画系统 (🔧 修复：动态获取武器信息)
   */
  private addTheaterAttackAnimationStep(timeline: Phaser.Time.Timeline, attackerSide: 'left' | 'right', attackerColor: string, attackerId?: string): void {
    timeline.add({
      at: 0,
      run: async () => {
        if (this.theaterDeathLock) { return; }
        // 优先使用分层（角色+武器）攻击动画；若失败则回退到基础攻击
        const pos = this.theaterWindow.getAgentScreenPosition(attackerSide);
        const characterType = this.mapColorToCharacterType(attackerColor);

        // 🔧 核心修复：Timeline执行时动态获取武器信息，而不依赖全局pendingWeaponType
        let weaponType: string | null = null;
        if (attackerId) {
          weaponType = this.weaponById.get(attackerId) || null;
        } else {
          weaponType = null;
        }

        const layeredId = `theater-${attackerSide}`;

        let layeredPlayed = false;
        try {
          if (pos) {
            // 暂时隐藏基础sprite，避免重影
            this.theaterWindow.setAgentVisible(attackerSide, false);
            let okFlag = false;
            // 若将精灵加入容器，需要将屏幕坐标转换为容器局部坐标
            const container = this.theaterWindow.getContainer();
            const localPos = container ? { x: pos.x - container.x, y: pos.y - container.y } : pos;
            // 仅在所有帧存在时使用分层ATTACK，否则直接回退分层Idle
            if (this.haveAllFrames(characterType, weaponType, AnimationType.ATTACK)) {
              // 播放分层攻击动画
              await new Promise<void>((resolve) => {
                this.spriteAnimationPlayer
                  .playLayeredAnimation(
                    layeredId,
                    characterType,
                    weaponType,
                    AnimationType.ATTACK,
                    localPos,
                    {
                      loop: false,
                      fps: 12,
                      flipX: attackerSide === 'left',
                      parentContainer: this.theaterWindow.getContainer() || undefined,
                      scale: this.theaterWindow.getBaseSpriteScale(attackerSide),
                      onComplete: () => resolve()
                    }
                  )
                  .then(ok => { okFlag = !!ok; if (!ok) resolve(); })
                  .catch(() => resolve());
              });
            } else {
              // 分层攻击帧不存在，回退基础攻击
              okFlag = false;
            }
            layeredPlayed = okFlag;
          }
        } catch (e) {
          console.warn('Layered weapon animation failed, fallback:', e);
        }

        // 若分层成功，保持分层精灵常驻（武器不隐藏）；否则恢复基础sprite
        if (!layeredPlayed) {
          this.theaterWindow.setAgentVisible(attackerSide, true);
          this.theaterWindow.playIdle(attackerSide);
        } else {
          this.theaterWindow.setAgentVisible(attackerSide, false);
          // 攻击完成后回到分层Idle（武器常驻）
          this.spawnOrPlayLayeredIdle(attackerSide, attackerColor);
        }

        if (!layeredPlayed) {
          const attackAnimKey = `${attackerColor}-atk`;
          await this.theaterWindow.playAttackAnimation(attackerSide, attackAnimKey).catch(() => {});
        }
        // 攻击播放后再次确保HP条位于前层
        try { this.theaterWindow.bringHPBarsToFront(); } catch {}
      }
    });
  }

  // 无武器的攻击：使用 rush（角色-only，无武器）
  private addTheaterRushAttackStep(timeline: Phaser.Time.Timeline, attackerSide: 'left' | 'right', attackerColor: string): void {
    timeline.add({
      at: 0,
      run: async () => {
        // 直接使用基础sprite播放 rush（无武器），更符合“无武器流程”且避免分层可见性问题
    const rushKey = `${attackerColor}-rush`;
        try {
          await this.theaterWindow.playAttackAnimation(attackerSide, rushKey);
        } catch {
          // 即便失败也不阻塞后续受击/HP
        }
      }
    });
  }

  /**
   * 剧场激活期间消费 AGENT_HEALTH_CHANGED 作为后备数据，当 DAMAGE 未附带 defenderRemainingHealth 时用于对齐 HP
   */
  public handleAgentHealthChanged(data: any): void {
    if (!this.theaterWindow.isTheaterActive() || !this.theaterSides) return;
    const agentId = String(data?.agentId || data?.id || '');
    if (!agentId) return;
    const cur = data?.current;
    const max = data?.max;
    if (typeof cur !== 'number' || typeof max !== 'number') return;
    const queue = this.hpQueue.get(agentId) || [];
    queue.push({ current: cur, max });
    this.hpQueue.set(agentId, queue);
  }

  /**
   * 剧院模式受击反馈
   */
  private addTheaterHurtAnimationStep(timeline: Phaser.Time.Timeline, defenderSide: 'left' | 'right'): void {
    timeline.add({
      at: ANIMATION_CONFIG.ATTACK_DURATION * ANIMATION_CONFIG.HURT_DELAY_RATIO,
      run: async () => {
        if (this.theaterDeathLock) { return; }
        // 优先使用分层（角色+武器）的HURT；失败则回退到基础剧院受击
        const pos = this.theaterWindow.getAgentScreenPosition(defenderSide);
        const container = this.theaterWindow.getContainer();
        const localPos = pos && container ? { x: pos.x - container.x, y: pos.y - container.y } : (pos || { x: 0, y: 0 });

        const defenderId = this.theaterSides ? (defenderSide === 'left' ? this.theaterSides.left : this.theaterSides.right) : '';
        const defenderColor = this.colorsById.get(defenderId) || this.fallbackColor(defenderId);
        const characterType = this.mapColorToCharacterType(defenderColor);
        const weaponType = this.weaponById.get(defenderId) || null; // 🔧 使用实际武器数据
        const layeredId = `theater-${defenderSide}`;

        let okFlag = false;
        try {
          if (pos) {
            if (this.hasWeapon(defenderId)) {
              this.theaterWindow.setAgentVisible(defenderSide, false);
              await new Promise<void>((resolve) => {
                this.spriteAnimationPlayer
                  .playLayeredAnimation(
                    layeredId,
                    characterType,
                    weaponType,
                    AnimationType.HURT,
                    localPos,
                    {
                      loop: false,
                      fps: 12,
                      flipX: defenderSide === 'left',
                      parentContainer: container || undefined,
                      scale: this.theaterWindow.getBaseSpriteScale(defenderSide),
                      onComplete: () => resolve()
                    }
                  )
                  .then(ok => { okFlag = !!ok; if (!ok) resolve(); })
                  .catch(() => resolve());
              });
            } else {
              // 无武器：用基础受击反馈
              this.theaterWindow.playHurtAnimation(defenderSide);
              okFlag = true;
            }
          }
        } catch (e) {
          console.warn('Layered HURT animation failed, fallback:', e);
        }

        if (!okFlag) {
          this.theaterWindow.playHurtAnimation(defenderSide);
        } else {
          // 受击结束后回到分层Idle
          const defenderId = this.theaterSides ? (defenderSide === 'left' ? this.theaterSides.left : this.theaterSides.right) : '';
          const defenderColor = this.colorsById.get(defenderId) || this.fallbackColor(defenderId);
          if (this.hasWeapon(defenderId)) this.spawnOrPlayLayeredIdle(defenderSide, defenderColor);
        }
      }
    });
  }

  /**
   * 剧院模式Miss文字显示步骤 (复用hurt动画的时机控制)
   */
  private addTheaterMissTextStep(timeline: Phaser.Time.Timeline, defenderSide: 'left' | 'right'): void {
    timeline.add({
      at: ANIMATION_CONFIG.ATTACK_DURATION * ANIMATION_CONFIG.HURT_DELAY_RATIO, // 🎯 复用hurt时机
      run: () => {
        // miss 文字步骤执行

        if (this.theaterDeathLock) {
          console.log(`❌ Miss文字被阻止: theaterDeathLock=true`);
          return;
        }

        // 🎯 方案2：直接在Theater容器内创建MISS文字
        const container = this.theaterWindow.getContainer();
        if (container) {
          const spacing = 200; // 默认间距，与Theater窗口的spacing一致
          const hpBarSpacing = spacing * 0.7; // HP条间距，与CombatTheaterWindow保持一致
          const hpBarYOffset = -40; // HP条在agent上方40px
          const missTextYOffset = hpBarYOffset + 15; // miss文字在HP条正下方15px

          // 根据defender方向计算容器内相对坐标，与HP条位置保持一致
          const missX = defenderSide === 'left' ? -hpBarSpacing / 2 : hpBarSpacing / 2;
          const missY = missTextYOffset;

          console.log(`📍 使用Theater容器内坐标: defenderSide=${defenderSide}, spacing=${spacing}`);
          // HP/Miss 文字偏移
          console.log(`📍 Miss文字容器内坐标: (${missX}, ${missY})`);

          // 🔧 直接在Theater容器内创建文字，确保层级正确
          const config = this.getEffectConfig('miss');
          if (config) {
            const textObj = this.scene.add.text(missX, missY, 'MISS!', {
              fontSize: config.fontSize,
              color: config.color
            });

            textObj.setOrigin(0.5);
            textObj.setDepth(1010); // 设置更高层级确保可见

            // 添加到Theater容器内
            container.add(textObj);

            // 创建动画效果
            this.scene.tweens.add({
              targets: textObj,
              y: textObj.y + config.tweenProps.y,
              alpha: config.tweenProps.alpha,
              duration: config.duration,
              onComplete: () => {
                container.remove(textObj);
                textObj.destroy();
              }
            });
          }

          // miss 文本位置（容器内）
          return;
        }

        // 回退方案：使用Theater窗口的agent位置
        const pos = this.theaterWindow.getAgentScreenPosition(defenderSide);
        // 回退到 theater agent 屏幕位置

        if (pos) {
          // miss文字显示在agent上方10像素（HP条下方）
          const missPos = {
            x: pos.x,
            y: pos.y - 10
          };

          this.createTextEffect('miss', 'MISS!', missPos);
          // miss 文本位置（agent 上方）
        } else {
          // 最终回退到固定位置
          this.createTextEffect('miss', 'MISS!', { x: 400, y: 200 });
          console.warn(`⚠️ 无法获取${defenderSide}侧位置，使用默认位置`);
        }
      }
    });
  }

  /**
   * 剧院模式死亡动画
   */
  private addTheaterDeathAnimationStep(timeline: Phaser.Time.Timeline, loserSide: 'left' | 'right', loserColor: string, combatId: string): void {
    timeline.add({
      at: 0,
      run: () => {
        // 异步动作开始：严格阻止提早收口
        this.incrementAsyncStep(combatId);
        try { ClientTrace.log('playback','playback','DEATH_ANIM_START', { details: { combatId, side: loserSide } }); } catch {}
        const deathAnimKey = `${loserColor}_die`;
        if (DEBUG_COMBAT) console.log(`💀 剧院死亡: ${loserSide}侧 播放 ${deathAnimKey}`);

        // 🔒 死亡闸门：立即清空战斗队列，防止后续事件继续播放
        this.theaterDeathLock = true;
        const q = this.combatQueues.get(combatId);
        if (q && q.length > 0) {
          console.log(`🔒 死亡闸门：清空队列 ${q.length} 个待处理事件 for combat ${combatId}`);
          q.length = 0; // 清空队列
        }
        this.combatBusy.delete(combatId);

        // 停止所有其他Timeline，避免被覆盖/打断
        this.executeOnAllTimelines((t) => {
          if (t !== timeline) {
            try { (t as any).stop?.(); } catch {}
            try { (t as any).destroy?.(); } catch {}
          }
        });
        // 仅停掉失败方的分层动画，保留胜者武器可见
        this.stopTheaterAnimationsForSide(loserSide);

        // 优先尝试：根据是否持武器选择死亡动画（持武器=分层die，无武器=角色-only die）
        const pos = this.theaterWindow.getAgentScreenPosition(loserSide);
        const container = this.theaterWindow.getContainer();
        const localPos = pos && container ? { x: pos.x - container.x, y: pos.y - container.y } : (pos || { x: 0, y: 0 });
        const characterType = this.mapColorToCharacterType(loserColor);
        const loserId = this.theaterSides ? (loserSide === 'left' ? this.theaterSides.left : this.theaterSides.right) : '';
        const weaponType = this.weaponById.get(loserId) || null; // 🔧 使用实际武器数据
        const layeredId = `theater-death-${loserSide}`; // 使用专用ID，避免与常驻分层冲突

        // 设置胜者展示：若持武器则维持/重建分层Idle（武器可见），否则使用基础idle
        const winnerSide: 'left' | 'right' = loserSide === 'left' ? 'right' : 'left';
        const winnerId = this.theaterSides ? (winnerSide === 'left' ? this.theaterSides.left : this.theaterSides.right) : '';
        const winnerColor = this.colorsById.get(winnerId) || this.fallbackColor(winnerId);
        if (this.hasWeapon(winnerId)) {
          this.spawnOrPlayLayeredIdle(winnerSide, winnerColor);
        } else {
          this.theaterWindow.setAgentVisible(winnerSide, true);
          this.theaterWindow.playIdle(winnerSide);
        }

        // 隐藏基础sprite避免重影
        this.theaterWindow.setAgentVisible(loserSide, false);

        const tryLayeredDeath = () => new Promise<boolean>((resolve) => {
          const useLayered = this.hasWeapon(this.theaterSides ? (loserSide === 'left' ? this.theaterSides.left : this.theaterSides.right) : '');
          const allOk = this.haveAllFrames(characterType, useLayered ? weaponType : undefined, AnimationType.DIE);
          if (!allOk) return resolve(false);
          const play = useLayered
            ? this.spriteAnimationPlayer.playLayeredAnimation(
                layeredId,
                characterType,
                weaponType,
                AnimationType.DIE,
                localPos,
                {
                  loop: false,
                  fps: 10,
                  flipX: loserSide === 'left',
                  parentContainer: container || undefined,
                  scale: this.theaterWindow.getBaseSpriteScale(loserSide),
                  onComplete: () => resolve(true)
                }
              )
            : this.spriteAnimationPlayer.playAnimation(
                layeredId,
                characterType,
                AnimationType.DIE,
                localPos,
                {
                  loop: false,
                  fps: 10,
                  flipX: loserSide === 'left',
                  parentContainer: container || undefined,
                  scale: this.theaterWindow.getBaseSpriteScale(loserSide),
                  onComplete: () => resolve(true)
                }
              );
          play.then(ok => { if (!ok) resolve(false); }).catch(() => resolve(false));
          // 超时保护：1.8s内未完成则回退
          this.scene.time.delayedCall(1800, () => resolve(false));
        });

        tryLayeredDeath().then((ok) => {
          if (ok) {
            // 分层死亡播放完成 → 标记完成并减少异步计数
            this.deathCompleted.add(combatId);
            this.decrementAsyncStep(combatId);
            if (DEBUG_COMBAT) console.log(`💀 剧院死亡动画完成: ${loserSide}侧`);
            try { ClientTrace.log('playback','playback','DEATH_ANIM_COMPLETE', { details: { combatId, side: loserSide, path: 'layered' } }); } catch {}
          } else {
            // 回退到基础剧院死亡：确保基础sprite可见
            try { this.theaterWindow.setAgentVisible(loserSide, true); } catch {}
            const p = this.theaterWindow.playDeathAnimation(loserSide, deathAnimKey)
              .then(() => { this.deathCompleted.add(combatId); })
              .catch((err) => { console.error(`❌ 剧院死亡动画失败(回退): ${loserSide}侧`, err); })
              .finally(() => {
                try { ClientTrace.log('playback','playback','DEATH_ANIM_COMPLETE', { details: { combatId, side: loserSide, path: 'fallback' } }); } catch {}
                this.decrementAsyncStep(combatId);
              });
            return p;
          }
        }).catch(()=>{
          try { ClientTrace.log('playback','playback','DEATH_ANIM_COMPLETE', { details: { combatId, side: loserSide, path: 'error' } }); } catch {}
          this.decrementAsyncStep(combatId);
        });
      }
    });
  }

  // 停止某侧前缀为 theater- 的分层动画（idle/attack/hurt），避免覆盖死亡
  private stopTheaterAnimationsForSide(side: 'left' | 'right'): void {
    const prefixList = [`theater-${side}`, `theater-death-${side}`];
    try {
      const ids = Array.from(this.spriteAnimationPlayer.activeAnimations.keys());
      ids.forEach(id => {
        if (prefixList.some(p => id.startsWith(p))) {
          try { this.spriteAnimationPlayer.stopAnimation(id); } catch {}
        }
      });
    } catch {}
  }

  /**
   * 关闭剧院模式并清理
   */
  private closeTheaterAndCleanup(combatId: string, deadAgentId?: string): void {
    if (DEBUG_COMBAT) console.log(`🎭 关闭剧院模式: ${combatId}, deadAgent: ${deadAgentId || 'none'}`);

    const timer = this.closeTimers.get(combatId);
    if (timer) {
      try { timer.remove(); } catch {}
      this.closeTimers.delete(combatId);
    }
    try {
      ClientTrace.log('playback','playback','CLOSE_REASON', {
        details: {
          combatId,
          baseReady,
          timeout,
          stepsRemaining,
          queueRemaining,
          asyncRemaining,
          sinceStep,
          elapsed,
          deathReady
        }
      });
    } catch {}
    this.pendingClosures.delete(combatId);
    this.lastStepCompletedAt.delete(combatId);
    this.asyncStepsInFlight.delete(combatId);
    this.deathCompleted.delete(combatId);

    // 🔒 立即隐藏主地图dead agent容器，防止剧院关闭到die动画之间的闪现
    if (deadAgentId) {
      const battleScene = this.scene as any;
      try {
        const container = battleScene?.phaserAnimationManager?.getAgentContainer?.(deadAgentId);
        if (container) {
          container.setVisible(false);
          console.log(`👻 已隐藏主地图agent容器: ${deadAgentId}`);
        }
      } catch (e) {
        console.warn(`⚠️ 隐藏agent容器失败: ${deadAgentId}`, e);
      }
    }

    // 🔒 先停止所有可能干扰的Timeline和分层动画，避免死亡期间的抖动
    this.executeOnAllTimelines((t) => {
      try { (t as any).stop?.(); } catch {}
      try { (t as any).destroy?.(); } catch {}
    });
    try { this.spriteAnimationPlayer.stopAnimation('theater-left'); } catch {}
    try { this.spriteAnimationPlayer.stopAnimation('theater-right'); } catch {}
    try { this.spriteAnimationPlayer.stopAnimation('theater-death-left'); } catch {}
    try { this.spriteAnimationPlayer.stopAnimation('theater-death-right'); } catch {}

    // 关闭战斗窗口（必须在触发主地图动画之前，避免剧场残留精灵干扰）
    this.theaterWindow.closeTheaterMode();

    // 🎯 剧场关闭后，延迟触发主地图死亡动画（显示容器后立即播放die）
    if (deadAgentId) {
      console.log(`💀 [延迟200ms] 触发主地图死亡动画: ${deadAgentId}`);
      try { ClientTrace.log('playback','playback','MAP_DEATH_START', { details: { combatId, agentId: deadAgentId } }); } catch {}
      this.scene.time.delayedCall(200, () => {
        const battleScene = this.scene as any;
        try {
          const container = battleScene?.phaserAnimationManager?.getAgentContainer?.(deadAgentId);
          if (container) {
            container.setVisible(true); // 显示容器后立即播放die动画
            console.log(`👁️ 已显示agent容器准备die动画: ${deadAgentId}`);
          }
        } catch {}
        this.playDeathAnimation(deadAgentId);
        try { ClientTrace.log('playback','playback','MAP_DEATH_TRIGGER', { details: { combatId, agentId: deadAgentId } }); } catch {}
      });
    }

    // 清理战斗资源
    this.cleanupCombat(combatId);
    this.theaterDeathLock = false;
    try { this.hpQueue.clear(); } catch {}
    this.stepsInFlight.delete(combatId);

    if (DEBUG_COMBAT) console.log(`✅ 剧院模式关闭完成: ${combatId}`);

    // 🛰️ 广播战斗整体结束（黑箱完成点）
    try {
      // ⏱️ 输出计时摘要
      try {
        const st = this.combatTiming.get(combatId);
        if (st) {
          st.closedAt = Date.now();
          const sum = summarizeCombatTiming(st);
          try { ClientTrace.log('playback','playback','COMBAT_TIMING', { details: { combatId, ...sum } }); } catch {}
          console.log(`⏱️ Combat ${combatId} timing: duration=${sum.durationMs}ms (damage=${sum.damageCount}, miss=${sum.missCount}, estimate=${sum.estimateMs}ms, diff=${sum.diffMs}ms)`);
        }
      } finally {
        this.combatTiming.delete(combatId);
      }
      try { ClientTrace.log('playback','playback','COMBAT_CLOSE', { details: { combatId } }); } catch {}
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ABR_COMBAT_ENDED', { detail: { combatId, timestamp: Date.now() } }));
      }
    } catch {}

    if (this.activeCombatId === combatId) {
      this.activeCombatId = null;
    }
  }

  /** 强制关闭当前剧场（例如 GAME_ENDED 时兜底，尊重最小可见时长与正在播放的步骤） */
  public forceCloseActiveCombat(reason: string = 'game_end'): void {
    if (!this.theaterWindow.isTheaterActive()) return;
    const cid = this.activeCombatId;
    if (!cid) return;
    this.requestCombatClosure(cid, undefined, reason);
  }

  private requestCombatClosure(combatId: string, loserId?: string, reason: string = 'combat_ended'): void {
    const existing = this.pendingClosures.get(combatId);
    if (!existing) {
      this.pendingClosures.set(combatId, { loserId, reason, requestedAt: Date.now() });
    } else if (loserId && !existing.loserId) {
      existing.loserId = loserId;
    }
    try {
      ClientTrace.log('playback','playback','CLOSE_GATE_OPEN', {
        details: {
          combatId,
          reason,
          loserId: loserId || existing?.loserId
        }
      });
    } catch {}
    this.ensureClosePolling(combatId);
  }

  private ensureClosePolling(combatId: string): void {
    if (this.closeTimers.has(combatId)) return;
    const timer = this.scene.time.addEvent({
      delay: CLOSE_POLL_INTERVAL_MS,
      loop: true,
      callback: () => {
        // 🔍 打点：收口检查（高频，但便于定位卡住的条件）
        try {
          const now = Date.now();
          const openedAt = this.theaterOpenAt.get(combatId) || now;
          const elapsed = now - openedAt;
          const stepsRemaining = this.stepsInFlight.get(combatId) || 0;
          const queueRemaining = (this.combatQueues.get(combatId) || []).length;
          const lastStep = this.lastStepCompletedAt.get(combatId) || openedAt;
          const sinceStep = now - lastStep;
          const asyncRemaining = this.getAsyncSteps(combatId);
          const pending = this.pendingClosures.get(combatId);
          const deathReady = !pending?.loserId || this.deathCompleted.has(combatId);
          ClientTrace.log('playback','playback','COMBAT_CLOSE_CHECK', {
            details: {
              combatId,
              elapsed,
              stepsRemaining,
              queueRemaining,
              asyncRemaining,
              sinceStep,
              deathReady,
              minVisibleMs: MIN_VISIBLE_MS,
              maxVisibleMs: MAX_VISIBLE_MS
            }
          });
        } catch {}
        if (this.evaluateCloseConditions(combatId)) {
          try { timer.remove(); } catch {}
          this.closeTimers.delete(combatId);
        }
      }
    });
    this.closeTimers.set(combatId, timer);
    this.evaluateCloseConditions(combatId);
  }

  private evaluateCloseConditions(combatId: string): boolean {
    const pending = this.pendingClosures.get(combatId);
    if (!pending) {
      return this.tryForceCloseWithoutPending(combatId);
    }
    const now = Date.now();
    const openedAt = this.theaterOpenAt.get(combatId) || now;
    const elapsed = now - openedAt;
    const stepsRemaining = this.stepsInFlight.get(combatId) || 0;
    const queueRemaining = (this.combatQueues.get(combatId) || []).length;
    const lastStep = this.lastStepCompletedAt.get(combatId) || openedAt;
    const sinceStep = now - lastStep;

    const asyncRemaining = this.getAsyncSteps(combatId);
    const deathReady = !pending.loserId || this.deathCompleted.has(combatId);
    const debounceOk = sinceStep >= CLOSE_DEBOUNCE_MS;
    const minVisibleOk = elapsed >= MIN_VISIBLE_MS;
    const stepsCleared = stepsRemaining === 0 && queueRemaining === 0 && asyncRemaining === 0;
    const baseReady = stepsCleared && debounceOk && deathReady && minVisibleOk;
    // 超时兜底：同样要求无在播动作（含 async），并通过死亡门
    const timeout = elapsed >= MAX_VISIBLE_MS && stepsRemaining === 0 && asyncRemaining === 0 && deathReady;

    if (!baseReady && !timeout) {
      const lastDebug = this.lastCloseDebugAt.get(combatId) || 0;
      if (now - lastDebug >= 500) {
        const queue = this.combatQueues.get(combatId) || [];
        const queueSample = queue.slice(0, 4).map(step => `${step.kind}:${step.seq}`);
        try {
          ClientTrace.log('playback','playback','COMBAT_QUEUE_STATE', {
            details: {
              combatId,
              stepsRemaining,
              queueRemaining,
              queueSample,
              queueOverflow: queue.length > queueSample.length,
              asyncRemaining,
              sinceStep,
              deathReady
            }
          });
        } catch {}
        this.lastCloseDebugAt.set(combatId, now);
      }
      return false;
    }

    this.pendingClosures.delete(combatId);
    const timer = this.closeTimers.get(combatId);
    if (timer) {
      try { timer.remove(); } catch {}
      this.closeTimers.delete(combatId);
    }
    this.closeTheaterAndCleanup(combatId, pending.loserId);
    return true;
  }

  private tryForceCloseWithoutPending(combatId: string): boolean {
    const openedAt = this.theaterOpenAt.get(combatId);
    if (!openedAt) return false;
    const now = Date.now();
    const elapsed = now - openedAt;
    const stepsRemaining = this.stepsInFlight.get(combatId) || 0;
    const queueRemaining = (this.combatQueues.get(combatId) || []).length;
    const asyncRemaining = this.getAsyncSteps(combatId);
    const lastStep = this.lastStepCompletedAt.get(combatId) || openedAt;
    const sinceStep = now - lastStep;
    const ready = elapsed >= MAX_VISIBLE_MS && stepsRemaining === 0 && queueRemaining === 0 && asyncRemaining === 0 && sinceStep >= CLOSE_DEBOUNCE_MS;
    if (!ready) return false;
    try { ClientTrace.log('playback','playback','COMBAT_TIMEOUT_CLOSE', { details: { combatId, elapsed } }); } catch {}
    this.closeTheaterAndCleanup(combatId);
    return true;
  }

  private incrementAsyncStep(combatId: string): void {
    const cur = this.asyncStepsInFlight.get(combatId) || 0;
    const next = cur + 1;
    this.asyncStepsInFlight.set(combatId, next);
    try { ClientTrace.log('playback','playback','COMBAT_ASYNC_STEP', { details: { combatId, count: next, action: 'inc' } }); } catch {}
  }

  private decrementAsyncStep(combatId: string): void {
    const cur = this.asyncStepsInFlight.get(combatId) || 0;
    const next = Math.max(0, cur - 1);
    if (next === 0) this.asyncStepsInFlight.delete(combatId);
    else this.asyncStepsInFlight.set(combatId, next);
    try { ClientTrace.log('playback','playback','COMBAT_ASYNC_STEP', { details: { combatId, count: next, action: 'dec' } }); } catch {}
  }

  private getAsyncSteps(combatId: string): number {
    return this.asyncStepsInFlight.get(combatId) || 0;
  }

  /**
   * 检查剧院模式状态
   */
  public isTheaterActive(): boolean {
    return this.theaterWindow.isTheaterActive();
  }

  /**
   * 查询某个agent是否处于当前活跃剧院中的参战双方
   */
  public isAgentInActiveCombat(agentId: string): boolean {
    if (!this.theaterWindow.isTheaterActive() || !this.theaterSides) return false;
    return this.theaterSides.left === agentId || this.theaterSides.right === agentId;
  }

  // 获取给定agent在当前事件中的颜色（优先使用事件参数）
  private colorForAgent(agentId: string, data: CombatEventData): string {
    if (agentId === data.attackerId) return data.attackerColor || this.fallbackColor(agentId);
    if (agentId === data.defenderId) return data.defenderColor || this.fallbackColor(agentId);
    return this.fallbackColor(agentId);
  }

  private fallbackColor(agentId: string): string {
    // 维持与旧逻辑兼容，尽量稳定返回 'green' or 'red'
    const type = getSlimeCharacterType(agentId);
    return typeof type === 'string' ? type : (agentId.includes('1') ? 'green' : 'red');
  }

  // ========= 内部辅助 =========
  private getTheaterSideForAgent(agentId: string): 'left' | 'right' {
    if (!this.theaterSides) return 'left';
    return this.theaterSides.right === agentId ? 'right' : 'left';
  }

  private mapColorToCharacterType(color: string): CharacterType {
    switch (color) {
      case 'green': return CharacterType.GREEN;
      case 'red': return CharacterType.RED;
      case 'purple': return CharacterType.PURPLE;
      case 'blue': return CharacterType.BLUE;
      case 'white': return CharacterType.WHITE;
      case 'black': return CharacterType.BLACK;
      case 'orange': return CharacterType.ORANGE;
      default: return CharacterType.GREEN;
    }
  }

  // ❌ 删除硬编码武器映射 - 使用实际事件数据
  // private weaponTypeForColor() - REMOVED: 违反事件驱动架构

  private hasWeapon(agentId: string): boolean {
    const w = this.weaponById.get(agentId);
    return !!w && w !== WeaponType.UNARMED;
  }

  private mapWeaponString(weapon: string): WeaponType {
    switch (weapon) {
      case 'SWORD': return WeaponType.SWORD;
      case 'AXE': return WeaponType.AXE;
      case 'LANCE': return WeaponType.LANCE;
      case 'UNARMED': return WeaponType.UNARMED;
      default: return WeaponType.UNARMED; // 默认为无武器，不是SWORD
    }
  }

  private haveAllFrames(characterType: CharacterType, weaponType: WeaponType | undefined, animationType: AnimationType): boolean {
    const frames = getFrameCount(animationType);
    for (let i = 0; i < frames; i++) {
      const charKey = getCharacterFrameKey(characterType, animationType, i);
      if (!this.scene.textures.exists(charKey)) return false;
      if (weaponType) {
        const wepKey = getWeaponFrameKey(weaponType, animationType, i);
        if (!this.scene.textures.exists(wepKey)) return false;
      }
    }
    return true;
  }

  // 生成或播放分层Idle（角色+武器），武器常驻
  private spawnOrPlayLayeredIdle(side: 'left' | 'right', color: string): void {
    const pos = this.theaterWindow.getAgentScreenPosition(side);
    const container = this.theaterWindow.getContainer();
    const localPos = pos && container ? { x: pos.x - container.x, y: pos.y - container.y } : (pos || { x: 0, y: 0 });
    const characterType = this.mapColorToCharacterType(color);
    const agentId = this.theaterSides ? (side === 'left' ? this.theaterSides.left : this.theaterSides.right) : '';
    const weaponType = this.weaponById.get(agentId) || null; // 🔧 使用实际武器数据
    const id = `theater-${side}`;

    if (!pos) return;

    // 若武器帧未预载，回退基础idle，避免报错
    const hasAll = this.haveAllFrames(characterType, weaponType || undefined, AnimationType.IDLE);
    if (!hasAll) {
      this.theaterWindow.setAgentVisible(side, true);
      this.theaterWindow.playIdle(side);
      return;
    }

    // 隐藏基础sprite并播放分层Idle（循环）
    this.theaterWindow.setAgentVisible(side, false);
    this.spriteAnimationPlayer
      .playLayeredAnimation(
        id,
        characterType,
        weaponType,
        AnimationType.IDLE,
        localPos,
        {
          loop: true,
          fps: 8,
          flipX: side === 'left',
          parentContainer: container || undefined,
          scale: this.theaterWindow.getBaseSpriteScale(side)
        }
      )
      .then(ok => { if (!ok) { this.theaterWindow.setAgentVisible(side, true); } })
      .catch(() => this.theaterWindow.setAgentVisible(side, true));
  }

  // 已移除 pendingWeaponType：统一在 Timeline 执行时从 weaponById 获取
}
